import { useState } from 'react';
import { FolderPicker } from '../../folderPicker/FolderPicker';
import { SelectedItemTags } from '../../folderPicker/SelectedItemTags';
import { copyResource } from '../../../apiServices/resourceApi';
import { serializeFuncs } from '../../../util/Sequentialize';
import { FinishedUploadsCounter } from '../fileUploadingModal/ProgressCounter';
import { useNotification } from '@/hooks/useNotification';
import { useAppDispatch } from '@/store/hooks';
import { invalidateParent } from '@/store/itemsSlice';
import { Button } from '../../ui/Button';
import { Spinner } from '../../ui/Spinner';

interface SelectedItem {
  id: string;
  [k: string]: unknown;
}

interface CopyItemModalProps {
  selectedItems: SelectedItem[];
  removeFromSelectedItems: (items: SelectedItem[]) => void;
  refreshList: (force?: boolean, unused?: undefined, hard?: boolean) => void;
  closeModal: () => void;
  updateIsQuerying: (querying: boolean) => void;
  workspaceName?: string;
  match?: { params: { id: string } };
}

export default function CopyItemModal({
  selectedItems,
  removeFromSelectedItems,
  refreshList,
  closeModal,
  updateIsQuerying,
  workspaceName,
  match,
}: CopyItemModalProps) {
  const [selectedDestination, setSelectedDestination] = useState<{ id: string } | 'root'>('root');
  const [copyStatus, setCopyStatus] = useState<string>('inactive');
  const [initialCount] = useState(selectedItems.length);
  const { notify } = useNotification();
  const dispatch = useAppDispatch();

  const updateSelectedDestination = (destination: { id: string } | undefined) => {
    if (destination) {
      setSelectedDestination(destination);
      setCopyStatus('active');
    } else {
      setSelectedDestination('root');
      setCopyStatus('inactive');
    }
  };

  const copyItem = async (file: SelectedItem) => {
    try {
      setCopyStatus('loading');
      updateIsQuerying(true);
      await copyResource(file.id, (selectedDestination as { id: string }).id);
      updateIsQuerying(false);
      setCopyStatus('inactive');
      removeFromSelectedItems([file]);
    } catch {
      setCopyStatus('error');
      notify({ type: 'error', message: 'Could not copy resource' });
      updateIsQuerying(false);
    }
  };

  const handleSubmit = async () => {
    if (match?.params.id === (selectedDestination as { id: string })?.id) {
      notify({ type: 'info', message: "Can't copy files to current folder" });
      return;
    }
    if (selectedItems.length === 1) {
      await copyItem(selectedItems[0]);
    } else {
      const funcs = selectedItems.map((item) => () => copyItem(item));
      await serializeFuncs(funcs);
    }
    refreshList(false, undefined, true);
    // The destination's cached list doesn't have the copies yet: fetch it again when it is opened
    dispatch(invalidateParent((selectedDestination as { id: string }).id));
    closeModal();
    notify({ type: 'success', message: selectedItems.length > 1 ? 'Items copied' : 'Item copied' });
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground">Copying</h4>
      <SelectedItemTags items={selectedItems} onRemove={removeFromSelectedItems} />
      <FolderPicker
        onSelectDestination={updateSelectedDestination}
        workspaceName={workspaceName}
        updateIsQuerying={updateIsQuerying}
        useCase="copy"
        currentFolderId={match?.params?.id}
      />
      <Button onClick={handleSubmit} disabled={selectedDestination === 'root' || copyStatus === 'loading'}>
        {copyStatus === 'loading' && <Spinner size="sm" />}
        {copyStatus === 'error' ? 'Try again' : 'Copy'}
      </Button>
      {initialCount > 1 && (
        <FinishedUploadsCounter
          filesListLength={initialCount}
          fileUploadingProcessStatus={copyStatus}
          finishedUploadsListLength={initialCount - selectedItems.length}
        />
      )}
    </div>
  );
}
